import { useCallback, useState } from 'react';
import { ArrowLeftRight } from 'lucide-react';
import { periodReportApi } from '@/api/reportCentre.api';
import { usePermission } from '@/hooks/usePermission';
import { Permissions } from '@/constants/permissions';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';
import { ReportToolbar, RunReportButton } from '@/components/reports/ReportToolbar';
import { AccountSearchInput } from '@/components/reports/AccountSearchInput';
import { exportToExcel, exportToCsvRows } from '@/utils/reportExport';
import type { ExcelColumn } from '@/utils/reportExport';
import {
  INPUT_CLS, LABEL_CLS, TH_CLS, TH_R_CLS, AMT_CLS, CARD_CLS,
  firstOfMonth, today, fmtDate, fmtAmtZero, fmtPct, EmptyState,
} from '@/components/analytics/AnalyticsKit';
import { CountChip, InfoBanner, errMessage } from './ReportCentreKit';
import type { OffsettingReportDto, OffsettingRow } from '@/types/reportCentre.types';

const REPORT_KEY = 'OFFSETTING';

const COMPOUND_CODE = 'COMPOUND_JOURNALS';

// ── Export ────────────────────────────────────────────────────────────────────

const EXCEL_COLUMNS: ExcelColumn[] = [
  { key: 'Code',   header: 'Offset Account', width: 16 },
  { key: 'Name',   header: 'Name',           width: 32 },
  { key: 'Dr',     header: 'Dr Movement',    width: 16, numFmt: '#,##0.00' },
  { key: 'Cr',     header: 'Cr Movement',    width: 16, numFmt: '#,##0.00' },
  { key: 'Net',    header: 'Net',            width: 16, numFmt: '#,##0.00' },
  { key: 'Count',  header: 'Txn Count',      width: 10 },
  { key: 'Pct',    header: '% of Activity',  width: 14 },
];

function buildExportRows(rows: OffsettingRow[]): Record<string, unknown>[] {
  return rows.map(r => ({
    Code:  r.offsetAccountCode,
    Name:  r.offsetAccountName,
    Dr:    r.debitMovement,
    Cr:    r.creditMovement,
    Net:   r.netMovement,
    Count: r.txnCount,
    Pct:   r.pctOfActivity,
  }));
}

// ── Component ─────────────────────────────────────────────────────────────────

export const OffsettingPage = () => {
  const { hasPermission } = usePermission();

  const [accountId, setAccountId] = useState('');
  const [accountLabel, setAccountLabel] = useState('');
  const [fromDate, setFromDate] = useState(firstOfMonth());
  const [toDate, setToDate] = useState(today());
  const [data, setData] = useState<OffsettingReportDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await periodReportApi.getOffsetting({ accountId, fromDate, toDate });
      setData(res.data?.data ?? null);
    } catch (e: unknown) {
      setError(errMessage(e));
    } finally {
      setLoading(false);
    }
  }, [accountId, fromDate, toDate]);

  if (!hasPermission(Permissions.RPT_LEDGER)) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
        You do not have permission to view this report.
      </div>
    );
  }

  const rows = data?.rows ?? [];

  const handleExcel = () => {
    if (!data) return;
    exportToExcel({
      sheetName: 'Offsetting',
      columns: EXCEL_COLUMNS,
      rows: buildExportRows(rows),
      title: `Offsetting Analysis — ${data.accountCode} ${data.accountName}`,
      subtitle: `${data.companyName} · ${data.fromDate} – ${data.toDate}`,
    }, `Offsetting_${data.accountCode}_${data.fromDate}_${data.toDate}`);
  };

  const handleCsv = () => {
    if (!data) return;
    exportToCsvRows(EXCEL_COLUMNS, buildExportRows(rows), `Offsetting_${data.accountCode}_${data.fromDate}_${data.toDate}`);
  };

  return (
    <div className="flex flex-col h-full gap-4 p-4 overflow-auto">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ArrowLeftRight size={22} className="text-brand-500" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Account Offsetting Analysis</h1>
        </div>
        <ReportToolbar
          onExcelExport={handleExcel}
          onCsvExport={handleCsv}
          hasData={!!data}
          reportKey={REPORT_KEY}
          rowCount={rows.length}
          filterSummary={`account=${accountLabel || accountId} from=${fromDate} to=${toDate}`}
        />
      </div>

      {/* Filter panel */}
      <div className={cn(CARD_CLS, 'p-4')}>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="w-80">
              <label className={LABEL_CLS}>Account *</label>
              <AccountSearchInput
                accountId={accountId || undefined}
                accountLabel={accountLabel || undefined}
                onChange={(id, code, name) => {
                  setAccountId(id);
                  setAccountLabel(`${code} — ${name}`);
                }}
                onClear={() => {
                  setAccountId('');
                  setAccountLabel('');
                }}
              />
            </div>
            <div className="w-40">
              <label className={LABEL_CLS}>From *</label>
              <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className={INPUT_CLS} />
            </div>
            <div className="w-40">
              <label className={LABEL_CLS}>To *</label>
              <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className={INPUT_CLS} />
            </div>
          </div>
          <RunReportButton
            onClick={() => { void run(); }}
            isLoading={loading}
            disabled={!accountId || !fromDate || !toDate}
          />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {loading && !data && (
        <div className="flex-1 flex items-center justify-center"><Spinner size="lg" /></div>
      )}

      {!data && !loading && (
        <EmptyState message='Pick an account and a date window, then click "Run Report" to see which accounts its activity offsets against.' />
      )}

      {data && (
        <div className="flex flex-col gap-3">
          {/* Meta + summary chips */}
          <div className={cn(CARD_CLS, 'px-4 py-3 flex flex-wrap items-center justify-between gap-3')}>
            <div>
              <div className="text-sm font-semibold text-gray-900 dark:text-white">
                {data.accountCode} — {data.accountName}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {data.companyName} · {fmtDate(data.fromDate)} – {fmtDate(data.toDate)} · Generated {fmtDate(data.generatedAt)}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <CountChip label="Two-line journals" count={data.summary.twoLineJournals} tone="blue" />
              <CountChip label="Compound journals" count={data.summary.compoundJournals} tone="grey" />
            </div>
          </div>

          {data.summary.compoundJournals > 0 && (
            <InfoBanner>
              Journals with more than two lines cannot be paired one-to-one; they are aggregated as compound journals.
            </InfoBanner>
          )}

          <div className={cn(CARD_CLS, 'flex flex-col min-h-0')}>
            <div className="overflow-auto">
              <table className="w-full text-sm border-collapse min-w-[1000px]">
                <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800 z-10">
                  <tr>
                    <th className={TH_CLS}>Offset Account</th>
                    <th className={TH_CLS}>Name</th>
                    <th className={TH_R_CLS}>Dr Movement</th>
                    <th className={TH_R_CLS}>Cr Movement</th>
                    <th className={TH_R_CLS}>Net</th>
                    <th className={TH_R_CLS}>Txn Count</th>
                    <th className={TH_R_CLS}>% of Activity</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-3 py-8 text-center text-sm text-gray-400 dark:text-gray-600">
                        No posted activity for this account in the selected window.
                      </td>
                    </tr>
                  ) : rows.map((r, i) => {
                    const isCompound = r.offsetAccountId === null || r.offsetAccountCode === COMPOUND_CODE;
                    const rowCls = isCompound
                      ? 'italic text-gray-500 dark:text-gray-400 bg-gray-50/60 dark:bg-gray-800/40'
                      : 'text-gray-900 dark:text-gray-100';
                    return (
                      <tr
                        key={r.offsetAccountId ?? `compound-${i}`}
                        className={cn('border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50', isCompound && 'bg-gray-50/60 dark:bg-gray-800/40')}
                      >
                        <td className={cn('px-3 py-2 text-xs font-mono whitespace-nowrap', isCompound ? 'italic text-gray-500 dark:text-gray-400' : 'text-gray-600 dark:text-gray-400')}>
                          {isCompound ? 'Compound journals' : r.offsetAccountCode}
                        </td>
                        <td className={cn('px-3 py-2 text-sm', rowCls)}>
                          {isCompound ? 'Aggregated multi-line journals (no one-to-one pairing)' : r.offsetAccountName}
                        </td>
                        <td className={cn(AMT_CLS, isCompound ? 'italic text-gray-500 dark:text-gray-400' : 'text-gray-700 dark:text-gray-300')}>
                          {fmtAmtZero(r.debitMovement)}
                        </td>
                        <td className={cn(AMT_CLS, isCompound ? 'italic text-gray-500 dark:text-gray-400' : 'text-gray-700 dark:text-gray-300')}>
                          {fmtAmtZero(r.creditMovement)}
                        </td>
                        <td className={cn(AMT_CLS, 'font-medium', isCompound ? 'italic text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-gray-100')}>
                          {fmtAmtZero(r.netMovement)}
                        </td>
                        <td className={cn(AMT_CLS, isCompound ? 'italic text-gray-500 dark:text-gray-400' : 'text-gray-700 dark:text-gray-300')}>
                          {r.txnCount.toLocaleString('en-US')}
                        </td>
                        <td className={cn(AMT_CLS, isCompound ? 'italic text-gray-500 dark:text-gray-400' : 'text-gray-700 dark:text-gray-300')}>
                          {fmtPct(r.pctOfActivity)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
